import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { IconType } from 'react-icons';
import { FaArrowLeft, FaArrowRight, FaLeaf } from 'react-icons/fa';
import VideoTitleWithOverlay, { VTOType } from '../../../../Components/VideoComponents/VideoTitleWithOverlay/VideoTitleWithOverlay';
import './ProjectShowcasePage.scss';

export type ProjectShowcaseTheme = 'oviv' | 'levivo' | 'simdev';

export type ProjectShowcaseMetaItem = {
    label: string;
    value: string;
    icon?: IconType;
};

export type ProjectShowcaseSlide = {
    image: string;
    eyebrow?: string;
    title: string;
    description: string;
};

type ProjectShowcaseAction = {
    label: string;
    onClick: () => void;
};

type ProjectShowcasePageProps = {
    theme: ProjectShowcaseTheme;
    logo: string;
    logoAlt: string;
    logoVariant?: 'wide' | 'mark';
    heroImage: string;
    heroVideo?: string;
    heroType?: VTOType;
    heroAction?: ProjectShowcaseAction;
    heroCaption?: string;
    introEyebrow: string;
    title: string;
    subtitle: string;
    introLead: string;
    introBody?: string[];
    meta: ProjectShowcaseMetaItem[];
    storyEyebrow: string;
    storyTitle: string;
    storyBody: string[];
    storyImage: string;
    storyImageAlt: string;
    storyAction?: ProjectShowcaseAction;
    secondaryStoryAction?: ProjectShowcaseAction;
    galleryEyebrow: string;
    galleryTitle: string;
    galleryDescription: string;
    slides: ProjectShowcaseSlide[];
    previousSlideLabel: string;
    nextSlideLabel: string;
    goToSlideLabel: string;
    featuresEyebrow: string;
    featuresTitle: string;
    featuresLead: string;
    features: string[];
    featuresConclusion?: string;
    asideEyebrow?: string;
    asideTitle?: string;
    asideSubtitle?: string;
    asideBody?: string;
    asideAction?: ProjectShowcaseAction;
};

export default function ProjectShowcasePage({
    theme,
    logo,
    logoAlt,
    logoVariant = 'wide',
    heroImage,
    heroVideo,
    heroType = VTOType.image,
    heroAction,
    heroCaption,
    introEyebrow,
    title,
    subtitle,
    introLead,
    introBody = [],
    meta,
    storyEyebrow,
    storyTitle,
    storyBody,
    storyImage,
    storyImageAlt,
    storyAction,
    secondaryStoryAction,
    galleryEyebrow,
    galleryTitle,
    galleryDescription,
    slides,
    previousSlideLabel,
    nextSlideLabel,
    goToSlideLabel,
    featuresEyebrow,
    featuresTitle,
    featuresLead,
    features,
    featuresConclusion,
    asideEyebrow,
    asideTitle,
    asideSubtitle,
    asideBody,
    asideAction
}: ProjectShowcasePageProps) {
    const [activeSlide, setActiveSlide] = useState(0);
    const hasCarousel = slides.length > 1;

    useEffect(() => {
        if (!hasCarousel) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setActiveSlide((current) => (current + 1) % slides.length);
        }, 5200);

        return () => window.clearInterval(intervalId);
    }, [hasCarousel, slides.length]);

    const nextSlide = () => {
        setActiveSlide((current) => (current + 1) % slides.length);
    };

    const previousSlide = () => {
        setActiveSlide((current) => (current - 1 + slides.length) % slides.length);
    };

    return (
        <div className={`project-showcase project-showcase--${theme}`}>
            <VideoTitleWithOverlay
                videoSource={heroVideo}
                imageSource={heroImage}
                overlayDelayInSeconds={2}
                type={heroType}
            >
                <motion.div
                    className="project-showcase-hero"
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.85, ease: 'easeOut', delay: 0.15 }}
                >
                    <img
                        className={`project-showcase-hero-logo project-showcase-hero-logo--${logoVariant}`}
                        src={logo}
                        alt={logoAlt}
                    />
                    {heroAction && (
                        <button className="project-showcase-button project-showcase-button--hero" onClick={heroAction.onClick}>
                            {heroAction.label}
                        </button>
                    )}
                    {heroCaption && <p className="project-showcase-hero-caption">{heroCaption}</p>}
                </motion.div>
            </VideoTitleWithOverlay>

            <main className="project-showcase-shell">
                <motion.section
                    className="project-showcase-intro"
                    initial={{ opacity: 0, y: 36 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.75, ease: 'easeOut' }}
                >
                    <div className="project-showcase-heading">
                        <span className="project-showcase-eyebrow">{introEyebrow}</span>
                        <h1>{title}</h1>
                        <p className="project-showcase-subtitle">{subtitle}</p>
                        <p className="project-showcase-lead">{introLead}</p>
                        {introBody.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                        ))}
                    </div>

                    <div className="project-showcase-meta">
                        {meta.map((item) => {
                            const Icon = item.icon;

                            return (
                                <div className="project-showcase-meta-item" key={`${item.label}-${item.value}`}>
                                    {Icon && (
                                        <div className="project-showcase-meta-icon">
                                            <Icon />
                                        </div>
                                    )}
                                    <div className="project-showcase-meta-copy">
                                        <span>{item.label}</span>
                                        <strong>{item.value}</strong>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.section>

                <section className="project-showcase-story">
                    <motion.div
                        className="project-showcase-story-copy"
                        initial={{ opacity: 0, x: -28 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        <span className="project-showcase-eyebrow">{storyEyebrow}</span>
                        <h2>{storyTitle}</h2>
                        {storyBody.map((paragraph, index) => (
                            <p key={`${paragraph}-${index}`}>{paragraph}</p>
                        ))}

                        {(storyAction || secondaryStoryAction) && (
                            <div className="project-showcase-story-actions">
                                {storyAction && (
                                    <button className="project-showcase-button project-showcase-button--primary" onClick={storyAction.onClick}>
                                        {storyAction.label}
                                    </button>
                                )}
                                {secondaryStoryAction && (
                                    <button className="project-showcase-button project-showcase-button--secondary" onClick={secondaryStoryAction.onClick}>
                                        {secondaryStoryAction.label}
                                    </button>
                                )}
                            </div>
                        )}
                    </motion.div>

                    <motion.figure
                        className="project-showcase-story-media"
                        initial={{ opacity: 0, x: 28 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        <img src={storyImage} alt={storyImageAlt} />
                    </motion.figure>
                </section>

                <motion.section
                    className="project-showcase-gallery"
                    initial={{ opacity: 0, y: 36 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <div className="project-showcase-gallery-head">
                        <div className="project-showcase-gallery-copy">
                            <span className="project-showcase-eyebrow">{galleryEyebrow}</span>
                            <h2>{galleryTitle}</h2>
                            <p>{galleryDescription}</p>
                        </div>

                        {hasCarousel && (
                            <div className="project-showcase-carousel-controls">
                                <button aria-label={previousSlideLabel} onClick={previousSlide}>
                                    <FaArrowLeft />
                                </button>
                                <button aria-label={nextSlideLabel} onClick={nextSlide}>
                                    <FaArrowRight />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="project-showcase-carousel-stage">
                        <AnimatePresence mode="wait">
                            <motion.figure
                                key={activeSlide}
                                className="project-showcase-carousel-slide"
                                initial={{ opacity: 0, scale: 1.02 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.99 }}
                                transition={{ duration: 0.55, ease: 'easeOut' }}
                            >
                                <img src={slides[activeSlide].image} alt={slides[activeSlide].title} />
                                <figcaption className="project-showcase-carousel-overlay">
                                    {slides[activeSlide].eyebrow && <span>{slides[activeSlide].eyebrow}</span>}
                                    <h3>{slides[activeSlide].title}</h3>
                                    <p>{slides[activeSlide].description}</p>
                                </figcaption>
                            </motion.figure>
                        </AnimatePresence>
                    </div>

                    {hasCarousel && (
                        <div className="project-showcase-carousel-progress">
                            {slides.map((slide, index) => (
                                <button
                                    key={`${slide.title}-${index}`}
                                    className={index === activeSlide ? 'is-active' : ''}
                                    onClick={() => setActiveSlide(index)}
                                    aria-label={`${goToSlideLabel} ${index + 1}`}
                                >
                                    <span className="project-showcase-carousel-index">{`0${index + 1}`}</span>
                                    <span className="project-showcase-carousel-meta">
                                        <strong>{slide.eyebrow ?? title}</strong>
                                        <span>{slide.title}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </motion.section>

                <section className="project-showcase-details">
                    <motion.div
                        className="project-showcase-features"
                        initial={{ opacity: 0, y: 36 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        <span className="project-showcase-eyebrow">{featuresEyebrow}</span>
                        <h2>{featuresTitle}</h2>
                        <p className="project-showcase-lead">{featuresLead}</p>

                        <div className="project-showcase-feature-list">
                            {features.map((feature) => (
                                <div className="project-showcase-feature-item" key={feature}>
                                    <FaLeaf />
                                    <span>{feature}</span>
                                </div>
                            ))}
                        </div>

                        {featuresConclusion && <p>{featuresConclusion}</p>}
                    </motion.div>

                    {(asideTitle || asideBody) && (
                        <motion.aside
                            className="project-showcase-aside"
                            initial={{ opacity: 0, y: 36 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.08 }}
                        >
                            {asideEyebrow && <span className="project-showcase-eyebrow">{asideEyebrow}</span>}
                            {asideTitle && <h3>{asideTitle}</h3>}
                            {asideSubtitle && <p className="project-showcase-aside-subtitle">{asideSubtitle}</p>}
                            {asideBody && <p>{asideBody}</p>}
                            {asideAction && (
                                <button className="project-showcase-button project-showcase-button--light" onClick={asideAction.onClick}>
                                    {asideAction.label}
                                </button>
                            )}
                        </motion.aside>
                    )}
                </section>
            </main>
        </div>
    );
}
